import React, { useCallback, useEffect, useMemo } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { RovoInlineChatPrompt } from '@atlassian/conversation-assistant-chat-prompt-input/ui/inline-chat-prompt';

import type { EditorPluginAIProvider } from '../../../../types/types';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';

const providerProductToProuductKey = (product: EditorPluginAIProvider['product']) => {
	switch (product) {
		case 'CONFLUENCE':
			return 'confluence';
		case 'JSW':
		case 'JSM':
		case 'JWM':
		case 'JPD':
			return 'jira';
		case 'ATLAS':
		case 'TOWNSQUARE':
			return 'atlas';
		case 'BITBUCKET':
			return 'bitbucket';
		default:
			return 'unknown';
	}
};

const backupChannelVariables = {
	cloudId: '',
	orgId: undefined,
};

interface RovoPromptScreenWithLogicProps {
	cancel: (options?: { shouldClearSelectionContext?: boolean }) => void;
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function RovoPromptScreenWithLogic({ cancel }: RovoPromptScreenWithLogicProps) {
	const {
		setIsRovoLoading,
		api,
		targetId,
		editorPluginAIProvider,
		appearance,
		setCancelRovoPrompt,
		popupsMountPoint,
	} = useContentComponentContext();

	const { cloudId, orgId } = useMemo(
		() => editorPluginAIProvider?.getChannelVariables?.('rovo-agent') || backupChannelVariables,
		[editorPluginAIProvider],
	);
	const product = editorPluginAIProvider.product;
	const productKey = providerProductToProuductKey(product);
	const isPageEditor =
		appearance === 'full-page' || appearance === 'full-width' || appearance === 'max';
	const source =
		productKey === 'confluence' && isPageEditor ? 'CONFLUENCE_INLINE_EDITOR' : 'INLINE_EDITOR';

	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);

	const showLabsLozenge = useMemo(() => {
		if (!fg('platform_editor_ai_aifc_patch_ga_blockers')) {
			return true;
		}

		if (productKey === 'confluence') {
			if (!fg('aifc_onboarding_ga_enabled')) {
				return true;
			}

			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			if (!FeatureGates.getExperimentValue('cc-aifc-databases-experiment', 'isEnabled', false)) {
				return true;
			}
		}

		return false;
	}, [productKey]);

	useEffect(() => {
		setCancelRovoPrompt?.(undefined);
		return () => {
			setIsRovoLoading(false);
		};
	}, [setCancelRovoPrompt, setIsRovoLoading]);

	const handleAbort = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			cancel({ shouldClearSelectionContext: streamEnded });
			setIsRovoLoading(false);
		},
		[cancel, setIsRovoLoading],
	);

	const onSubmit = useCallback(
		(cancel: () => void) => {
			editorPluginAIProvider.onSubmit?.();
			setIsRovoLoading(true);

			// Dispatch stream event to set status to 'streaming' immediately
			// This ensures the UI state transitions to 'loading' without waiting for nextSessionId
			if (fg('platform_editor_ai_migrate_from_xstate')) {
				api?.aiExperience?.actions?.dispatchAIEvent?.({
					type: 'stream',
					adfResponse: undefined, // Will be updated when actual streaming starts
				});
			}

			const cancelRovoPrompt = () => {
				cancel();
			};
			// give DOM time to update the screen
			setTimeout(() => {
				setCancelRovoPrompt?.(() => cancelRovoPrompt);
			}, 100);
		},
		[api?.aiExperience?.actions, editorPluginAIProvider, setCancelRovoPrompt, setIsRovoLoading],
	);

	// If a session is active - we'll show the loading screen elsewhere and we can hide this
	if (nextSessionId) {
		return null;
	}

	return (
		<FlagsProvider>
			<RovoInlineChatPrompt
				source={
					fg('platform_editor_aifc_in_page_comment')
						? source
						: productKey === 'confluence'
							? 'CONFLUENCE_INLINE_EDITOR'
							: 'INLINE_EDITOR'
				}
				productKey={productKey}
				siteId={cloudId}
				orgId={orgId}
				objectId={targetId}
				onAbort={handleAbort}
				onCancel={() => {
					/* noop - this is to allow the rovo inline chat to manage cancel behaviour.
					 * We shouldn't end the AI experience directly. */
				}}
				popupsMountPoint={fg('platform_editor_fix_inline_rendering') ? popupsMountPoint : undefined}
				// The following nops usages are like this because we want to ensure that only the AI plugin or the Orchestrator
				// are controlling the xstate. We do not want this to end the ai experience directly. Instead the rovo bridge
				// will call the orchestrator which will then end the experience, on both complete and abort.
				// IMPORTANT!! Do not end the AI experience on the complete callback. This can cause problems because the
				// rovo bridge may still have events to stream in the queue, and it's possible that this could result in
				// the experience being ended immediately on mount, which would then unmount the prompt. This is because the rovo bridge
				// can have a final response in its queue which has never fluushed previously, because something else unmounted this
				// component before the final response was flushed.
				onComplete={() => {
					setIsRovoLoading(false);
				}}
				onSubmit={onSubmit}
				enableAgents={editorPluginAIProvider.isRovoEnabled && fg('platform_inline_rovo_agents')}
				_showLabsLozenge={showLabsLozenge}
			/>
		</FlagsProvider>
	);
}
